import type { RawAuthUser } from '@/store/authStore';
import type { ClassCategoryDraft } from '@/features/profile/classes/classCategoryTypes';
import type { OwnedClassCard } from '@/features/profile/types';

/** The login `boardWithCategory` marker for "not in any named category". */
export const UNCATEGORISED_CATEGORY_ID = -1;

/** A single board→category placement to persist via `addclass`. */
export interface ClassAssignment {
  readonly boardId: number;
  /** Target category id; `-1` == uncategorised. */
  readonly categoryId: number;
  /**
   * 1-based slot within the target bucket. The backend rejects `0`
   * (`"Position is postive integer value"`), so positions start at 1.
   */
  readonly position: number;
}

interface DraftLike {
  readonly categories: readonly Pick<ClassCategoryDraft, 'id' | 'classIds'>[];
  readonly uncategorizedClassIds: readonly string[];
}

type Placement = Map<number, { categoryId: number; position: number }>;

/**
 * Build a `boardId → { categoryId, position }` map for one side of the diff.
 * Categories with a non-numeric id (a freshly typed `cat-<timestamp>` row that
 * has no real backend id yet) yield `NaN` category ids; callers must skip those.
 *
 * Positions are 1-based: the backend's `addclass` endpoint requires a positive
 * integer and rejects `0`.
 */
function placementFrom(draft: DraftLike): Placement {
  const map: Placement = new Map();
  for (const category of draft.categories) {
    const categoryId = Number(category.id);
    category.classIds.forEach((classId, index) => {
      const boardId = Number(classId);
      if (Number.isFinite(boardId)) {
        map.set(boardId, { categoryId, position: index + 1 });
      }
    });
  }
  draft.uncategorizedClassIds.forEach((classId, index) => {
    const boardId = Number(classId);
    if (Number.isFinite(boardId) && !map.has(boardId)) {
      map.set(boardId, { categoryId: UNCATEGORISED_CATEGORY_ID, position: index + 1 });
    }
  });
  return map;
}

/**
 * Diff the original (login) layout against the edited draft and emit an
 * assignment for every board whose `(categoryId, position)` changed.
 *
 * Targets whose category id is not a finite number (unsaved `cat-…` rows) are
 * skipped — there's no backend category to assign them to yet.
 */
export function buildAssignmentDiff(original: DraftLike, draft: DraftLike): ClassAssignment[] {
  const before = placementFrom(original);
  const after = placementFrom(draft);

  const out: ClassAssignment[] = [];
  for (const [boardId, target] of after) {
    if (!Number.isFinite(target.categoryId)) {
      continue;
    }
    const prev = before.get(boardId);
    // Reordering within the uncategorised bucket is handled by the class-sort
    // endpoint. It is not a new category assignment, so Save must not emit an
    // addclass request with the pseudo-category id (-1).
    if (
      prev?.categoryId === UNCATEGORISED_CATEGORY_ID &&
      target.categoryId === UNCATEGORISED_CATEGORY_ID
    ) {
      continue;
    }
    if (!prev || prev.categoryId !== target.categoryId || prev.position !== target.position) {
      out.push({ boardId, categoryId: target.categoryId, position: target.position });
    }
  }
  return out;
}

/** Minimal board row kept inside `rawUser.boardWithCategory[].boards`. */
interface RawBoardRow {
  id: number;
  code?: string;
  name?: string;
  role?: string;
  position?: number;
  [key: string]: unknown;
}

/** Index every board currently inside `rawUser.boardWithCategory` by numeric id. */
function indexExistingBoards(rawUser: RawAuthUser | null): Map<number, RawBoardRow> {
  const map = new Map<number, RawBoardRow>();
  const groups = rawUser?.boardWithCategory;
  if (!Array.isArray(groups)) return map;
  for (const group of groups) {
    const boards = (group as { boards?: unknown })?.boards;
    if (!Array.isArray(boards)) continue;
    for (const board of boards) {
      if (board && typeof board === 'object') {
        const id = Number((board as { id?: unknown }).id);
        if (Number.isFinite(id)) {
          map.set(id, { ...(board as RawBoardRow), id });
        }
      }
    }
  }
  return map;
}

/**
 * Rebuild `rawUser.boardWithCategory[]` from the edited draft so the profile
 * "Your classes" section (which renders straight from the login payload)
 * reflects the new grouping immediately — reassigned classes leave the
 * uncategorised section and appear under their category.
 *
 * Each board's other fields (`code`, `name`, `role`, `position`) are preserved
 * by looking it up in the existing payload; `allCards` provides a fallback for
 * `code`/`name`. One group is emitted per draft category (boards in `classIds`
 * order) plus a single uncategorised group (`categoryId: -1`) for the rest.
 *
 * Any board that exists in the current payload but isn't referenced by the
 * draft (e.g. a board the manage modal doesn't track) is preserved by being
 * appended to the uncategorised group, so nothing ever disappears from the
 * profile after a save.
 */
export function applyAssignmentsToRawUser(
  rawUser: RawAuthUser | null,
  draftCategories: readonly Pick<ClassCategoryDraft, 'id' | 'name' | 'classIds'>[],
  uncategorizedClassIds: readonly string[],
  allCards: readonly OwnedClassCard[],
): RawAuthUser {
  const existingById = indexExistingBoards(rawUser);
  const cardById = new Map(allCards.map((card) => [card.id, card]));
  const placedIds = new Set<number>();

  const resolveBoard = (classId: string, position: number): RawBoardRow | null => {
    const id = Number(classId);
    if (!Number.isFinite(id)) return null;
    // A board can only live in one bucket — skip if already placed so it never
    // renders twice (e.g. if the draft listed it in a category AND uncategorised).
    if (placedIds.has(id)) return null;
    placedIds.add(id);
    const existing = existingById.get(id);
    if (existing) return { ...existing, position };
    const card = cardById.get(classId);
    if (!card) return null;
    return { id, code: card.code, name: card.name, role: 'owner', position };
  };

  const groups: Array<Record<string, unknown>> = [];

  draftCategories.forEach((category) => {
    const categoryId = Number(category.id);
    if (!Number.isFinite(categoryId)) return; // skip unsaved rows
    const boards = category.classIds
      .map((classId, index) => resolveBoard(classId, index + 1))
      .filter((board): board is RawBoardRow => Boolean(board));
    groups.push({
      categoryId,
      categoryName: category.name.trim(),
      boards,
      archive: false,
    });
  });

  const uncategorizedBoards = uncategorizedClassIds
    .map((classId, index) => resolveBoard(classId, index + 1))
    .filter((board): board is RawBoardRow => Boolean(board));

  // Preserve any existing board the draft didn't account for (so a save never
  // silently drops a class) by keeping it in the uncategorised bucket.
  let nextPosition = uncategorizedBoards.length + 1;
  for (const [id, board] of existingById) {
    if (!placedIds.has(id)) {
      uncategorizedBoards.push({ ...board, position: nextPosition });
      nextPosition += 1;
    }
  }

  groups.push({
    categoryId: UNCATEGORISED_CATEGORY_ID,
    categoryName: null,
    boards: uncategorizedBoards,
    archive: false,
  });

  return { ...(rawUser ?? {}), boardWithCategory: groups };
}
